import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TourKey } from '../types/tour';

interface TourState {
  /** 現在発火中のツアー (null = 非表示) */
  activeTourKey: TourKey | null;
  /** 完了 or skip 済みツアーキーの配列 */
  visitedTours: TourKey[];
  /** ツアー一時停止中フラグ (route 遷移時に利用) */
  paused: boolean;

  /** 未訪問の場合のみツアー発火。訪問済みなら no-op */
  startTour: (key: TourKey) => void;
  /** 訪問済みでも強制発火 (手動再実行用) */
  forceStartTour: (key: TourKey) => void;
  /** 完了マーク + 非表示化 */
  completeTour: (key: TourKey) => void;
  /** skip マーク (visitedTours に追加し再発火を防止) */
  skipTour: (key: TourKey) => void;
  /** 一時停止 / 再開 */
  pauseTour: () => void;
  resumeTour: () => void;
  /** サーバー側 visited_features と同期 (重複なくマージ) */
  hydrateVisited: (serverVisited: TourKey[]) => void;
  /** 判定 helper */
  isVisited: (key: TourKey) => boolean;
}

export const useTourStore = create<TourState>()(
  persist(
    (set, get) => ({
      activeTourKey: null,
      visitedTours: [],
      paused: false,

      startTour: (key) => {
        if (get().visitedTours.includes(key)) return;
        set({ activeTourKey: key, paused: false });
      },

      forceStartTour: (key) => {
        set({ activeTourKey: key, paused: false });
      },

      completeTour: (key) => {
        set((state) => ({
          activeTourKey: state.activeTourKey === key ? null : state.activeTourKey,
          visitedTours: state.visitedTours.includes(key)
            ? state.visitedTours
            : [...state.visitedTours, key],
        }));
      },

      skipTour: (key) => {
        set((state) => ({
          activeTourKey: state.activeTourKey === key ? null : state.activeTourKey,
          visitedTours: state.visitedTours.includes(key)
            ? state.visitedTours
            : [...state.visitedTours, key],
        }));
      },

      pauseTour: () => set({ paused: true }),
      resumeTour: () => set({ paused: false }),

      hydrateVisited: (serverVisited) => {
        set((state) => {
          const merged = Array.from(new Set([...state.visitedTours, ...serverVisited]));
          return { visitedTours: merged as TourKey[] };
        });
      },

      isVisited: (key) => get().visitedTours.includes(key),
    }),
    {
      name: 'rtm-tour-storage',
      partialize: (state) => ({ visitedTours: state.visitedTours }),
    },
  ),
);
